<template>
    <div class="download-modal__select-wrapper">
        Format
        <Select
            class="platform-select"
            :model-value="modelValue"
            data-testid="asset-download__format-select"
            @onChange="$emit('update:modelValue', $event)"
        >
            <Option
                v-for="platform in DownloadPlatformsList"
                :key="platform"
                class="platform-select__option"
                :value="platform"
                :disabled="isDisabled(platform)"
                :class="{
                    'platform-select__option--divider': isDividing(platform as (typeof DividingPlatforms)[number])
                }"
                :data-testid="`download-modal__platform-select__option-${platform}`"
            >
                {{ DownloadPlatformsLabels[platform] }}
            </Option>
        </Select>
    </div>
</template>

<script lang="ts">
import type { PropType } from "vue";

import { PLATFORM, DOWNLOAD_FORMAT, PSD_DOWNLOAD_FORMATS, type DownloadFormatType } from "hoxton-shared/enums/exports";

import { DownloadPlatformsLabels } from "@/enums/platforms";

const DownloadPlatformsList = [
    PLATFORM.DOUBLECLICK,
    PLATFORM.DOUBLECLICK_SV,
    PLATFORM.OPENDC,
    PLATFORM.FLASHTALKING,
    PLATFORM.FLASHTALKING_EXCEL,
    PLATFORM.FLASHTALKING_ASSETS,

    DOWNLOAD_FORMAT.GENERIC,
    DOWNLOAD_FORMAT.GENERIC_LITE,
    DOWNLOAD_FORMAT.IMAGE_NO_SCALE,
    DOWNLOAD_FORMAT.IMAGE,
    DOWNLOAD_FORMAT.IMAGE_JPEG_NO_SCALE,
    DOWNLOAD_FORMAT.IMAGE_JPEG,
    DOWNLOAD_FORMAT.PSD,
    DOWNLOAD_FORMAT.VIDEO
];

const DividingPlatforms = [
    DOWNLOAD_FORMAT.OPENDC,
    DOWNLOAD_FORMAT.FLASHTALKING,
    DOWNLOAD_FORMAT.GENERIC,
    DOWNLOAD_FORMAT.GENERIC_LITE,
    DOWNLOAD_FORMAT.IMAGE_NO_SCALE,
    DOWNLOAD_FORMAT.PSD,
    DOWNLOAD_FORMAT.VIDEO
];

export default {
    props: {
        modelValue: {
            required: true,
            type: String as PropType<DownloadFormatType | "">
        },
        showHtmlOptions: {
            required: true,
            type: Boolean
        },
        showPsdOptions: {
            required: true,
            type: Boolean
        },
        showAeOptions: {
            required: true,
            type: Boolean
        }
    },

    emits: ["update:modelValue"],

    data() {
        return {
            DownloadPlatformsList: DownloadPlatformsList,
            DownloadPlatformsLabels: DownloadPlatformsLabels as Record<string, string>,
            DividingPlatforms: DividingPlatforms
        };
    },

    computed: {
        clientId() {
            return this.$route.params.clientId as string;
        },

        campaignId() {
            return this.$route.params.campaignId as string;
        }
    },

    created() {
        if (
            this.$auth.userCan(this.$auth.Actions.CanDownloadVideo, {
                clientId: this.clientId,
                campaignId: this.campaignId
            })
        ) {
            this.DownloadPlatformsList.filter(platform => platform !== DOWNLOAD_FORMAT.VIDEO);
        }
    },

    methods: {
        isDisabled(option: string) {
            // After Effects banners can be only downloaded as video for now
            if (this.showAeOptions && option !== DOWNLOAD_FORMAT.VIDEO) {
                return true;
            }

            // HTML banners cannot export a PSD file
            if (this.showHtmlOptions && option === DOWNLOAD_FORMAT.PSD) {
                return true;
            }

            if (
                this.showPsdOptions &&
                !Object.values(PSD_DOWNLOAD_FORMATS as Record<string, string>).includes(option)
            ) {
                return true;
            }

            return false;
        },

        isDividing(option: (typeof DividingPlatforms)[number]) {
            return this.DividingPlatforms.includes(option);
        }
    }
};
</script>

<style lang="scss">
@use "@style/variables" as *;

.download-modal__select-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 0 $spacing;
    width: 500px;
}

.platform-select {
    flex: 1 0;
    width: 100%;
    margin-left: 20px;
    &__option {
        border-top: 1px solid transparent;

        &--divider {
            border-top: 1px solid $grey3;
            margin-top: 5px;
            padding-top: 7px;

            &:before {
                content: "";
                height: 5px;
                display: block;
                background-color: $white;
                width: calc(100% + 2 * 16px);
                margin-left: -$spacing;
                position: relative;
                top: -7px;
            }
        }
    }
}
</style>
